import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnDestroy, OnInit, Optional, signal } from '@angular/core';
import { Feature, Map as OlMap } from 'ol';
import { Geometry } from 'ol/geom';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Fill, Stroke, Style, Text } from 'ol/style';
import CircleStyle from 'ol/style/Circle';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { FeatureLike } from 'ol/Feature';

import { ImportWizardStorage, ImportWizardStep } from '../../../../import-wizard.storage';
import { ImportWizardComponent } from '../../../../import-wizard.component';
import { ImportWizardGeoSpatialService } from '../../import-wizard-geospatial.service';
import { ImportWizardPendingService } from '../../../pending-list/import-wizard-pending-list.service';
import { ImportWizardPendingStorage } from '../../../pending-list/import-wizard-pending-list.storage';
import { SharedImportsStorage } from 'src/app/pages/shared-imports/shared-imports.storage';
import { isHTML } from 'src/app/utils/string.utils';
import { ImportWizardGeospatialMinimapService } from './import-wizard-geospatial-minimap.service';
import { DatasetType } from '@/app/dtos/import-wizard/import-wizard.dto';

import { getCssVar } from '../../../../../../../../../utils/css.utils';

@Component({
    selector: 'app-import-wizard-geospatial-minimap',
    template: `
        <div id="map" class="map"></div>
    `,
    styleUrls: ['./import-wizard-geospatial-minimap.component.scss'],
    imports: [
        CommonModule
    ],
    providers: [ImportWizardGeospatialMinimapService],
    standalone: true
})
export class ImportWizardGeoSpatialMiniMapComponent implements OnInit, OnDestroy {

    
    private minimapService = inject(ImportWizardGeospatialMinimapService);

    /*
    private getCssVar(name: string): string{
        return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    }
    */ 
   
   _state = computed(() => this.importWizardStorage.state());
    isLoading = signal<boolean>(true);
    isFileLoading = signal<boolean>(true);

    private vectorLayers: VectorLayer<VectorSource>[] = [];

    // Referência ao mapa via service
    private get map(): OlMap {
        return this.minimapService.map;
    }

    constructor(
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private importWizardStorage: ImportWizardStorage,
        private importWizardGeospatialService: ImportWizardGeoSpatialService,
        private importWizardPendingService: ImportWizardPendingService,
        private importWizardPendingStorage: ImportWizardPendingStorage,
        private sanitizer: DomSanitizer,
        @Optional() private dialogRef: MatDialogRef<ImportWizardComponent>,
        private toastrService: ToastrService,
        private sharedImportsStorage: SharedImportsStorage
    ) {}

    async ngOnInit() {
        this.minimapService.onFeaturesLoaded = (features) => {
            this.addFeaturesToMap(features);
        };

        this.minimapService.onFeaturesKMLLoaded = (featuresFile) => {
            this.addFeaturesToMap(featuresFile);
        };

        await this.importWizardGeospatialService.useGetImportFile();
        this.isFileLoading.set(false);

        await this.minimapService.retrieveFileData();
        this.initCursorHandler();
    }

    private addFeaturesToMap(features: Feature<Geometry>[]): void {
        try {

            const vectorSource = new VectorSource({
                features: features,
            });

            const primaryColor =
                getCssVar('--color-primary') ||
                getComputedStyle(document.body).getPropertyValue('--color-primary').trim() ||
                '#F5710b';

            const baseStyle = new Style({
                fill: new Fill({
                    color: 'rgba(255, 195, 0, 0.3)',
                }),
                stroke: new Stroke({
                    color: primaryColor,
                    width: 2,
                }),
                image: new CircleStyle({
                    radius: 4,
                    fill: new Fill({
                        color: primaryColor,
                    }),
                    stroke: new Stroke({
                        color: '#ffffff',
                        width: 1,
                    }),
                }),
            });

            const styleFunction = (feature: FeatureLike) => {
                const styles = [baseStyle];
                const name = feature.get('name');
                const geometry = feature.getGeometry();
                const geometryType = geometry?.getType();

                if (name && (geometryType === 'Point' || geometryType === 'MultiPoint')) {
                    styles.push(new Style({
                        text: new Text({
                            text: name.toString(),
                            font: 'bold 12px sans-serif',
                            fill: new Fill({ color: '#000000' }),
                            stroke: new Stroke({ color: '#ffffff', width: 3 }),
                            offsetY: -15,
                            overflow: true
                        })
                    }));
                }

                return styles;
            };

            const vectorLayer = new VectorLayer({
                source: vectorSource,
                style: styleFunction,
            });

            this.map.addLayer(vectorLayer);
            this.vectorLayers.push(vectorLayer);

            this.map.getView().fit(vectorSource.getExtent(), {
                padding: [20, 20, 140, 20],
                duration: 0,
            });
        } catch (error) {
            this.toastrService.error(
                'Erro ao carregar arquivo!',
                'Houve um problema com o arquivo enviado (verifique-o e tente novamente).',
                {
                    progressBar: true,
                    closeButton: true
                }
            );

            this.importWizardPendingService
                .deleteInProgressImport(this.importWizardStorage.state().id)
                .subscribe();

            if (this.importWizardStorage.state().isShared) {
                this.router.navigateByUrl(this.sharedImportsStorage.sharedImport()?.import_link ?? '/');
            } else {
                this.refetchPendingImports();
            }

            if (this.dialogRef) {
                this.dialogRef.close();
            }
        }
    }

    private refetchPendingImports() {
        this.importWizardPendingService.useGetAllPendingImports(DatasetType.SPATIAL).subscribe({
            next: (data) => {
                this.importWizardPendingStorage.setPendingImports(data);
            },
            error: (error) => {
                console.error(error);
            }
        });
    }

    private initCursorHandler() {
        const map = this.map as any;
        const target = this.map.getTargetElement();

        enum MouseButton {
            LEFT = 0,
            MIDDLE = 1,
            RIGHT = 2,
        }

        map.on('pointerdrag', () => {
            target.style.cursor = 'grabbing';
        });

        map.on('pointerdown', (evt) => {
            if (evt.originalEvent.button === MouseButton.LEFT) {
                target.style.cursor = 'grabbing';
            }
        });

        map.on('pointerup', () => {
            target.style.cursor = 'default';
        });

        map.on('pointermove', () => {
            target.style.cursor = 'default';
        });
    }

    hasProperties(feature: any): boolean {
        const keys = Object.keys(feature);
        if (keys.length === 0) return false;
        if (keys.length === 1 && keys[0] === 'styleUrl' && !(feature.name || feature._name)) return false;
        return true;
    }

    ngOnDestroy(): void {
        if (this.minimapService.map) {
            this.minimapService.map.setTarget(undefined);
        }
    }

    get isPreviewStep(): boolean {
        return this._state().step === ImportWizardStep.PREVIEW;
    }

    ImportWizardStep = ImportWizardStep;
    isHTML = isHTML;
}